import { useEffect, useRef, useCallback } from 'react';
import { playSound } from '../utils/sounds';
import { getWsBase } from '../native/tenant';

interface CADEvent {
  event: string;
  data: any;
}

export interface SoundConfig {
  sound_dispatch_new_call?: string;
  sound_dispatch_status_change?: string;
  sound_mobile_call_assigned?: string;
  sound_mobile_call_update?: string;
}

export type ScreenMode = 'dispatch' | 'mobile' | 'admin';

export default function useCADWebSocket(
  onUpdate: () => void,
  mode: ScreenMode = 'dispatch',
  soundConfig?: SoundConfig,
  onRawEvent?: (event: string, data: any) => void,
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closingRef = useRef(false);
  // Handlers live in refs so a caller passing inline callbacks (a new identity
  // every render) can't recycle the socket and drop events that arrive during
  // the reconnect window.
  const onUpdateRef = useRef(onUpdate);
  const soundConfigRef = useRef(soundConfig);
  const onRawEventRef = useRef(onRawEvent);
  onUpdateRef.current = onUpdate;
  soundConfigRef.current = soundConfig;
  onRawEventRef.current = onRawEvent;

  const connect = useCallback(() => {
    // Tear down any existing socket before opening a new one so a dependency
    // change (e.g. soundConfig loading) can never leave two live connections
    // delivering duplicate events / sounds.
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    closingRef.current = false;
    const token = localStorage.getItem('token');
    if (!token) {
      // The server rejects unauthenticated sockets; wait for a login.
      reconnectTimer.current = setTimeout(connect, 3000);
      return;
    }
    const url = `${getWsBase()}/api/ws?mode=${mode}`;
    // Pass the JWT via the WebSocket subprotocol (Sec-WebSocket-Protocol header)
    // instead of the URL, so it never appears in access logs. The server echoes
    // back the "bearer" subprotocol to complete the handshake.
    const ws = new WebSocket(url, ['bearer', token]);

    ws.onopen = () => {
      console.log('CAD WebSocket connected');
    };

    ws.onmessage = (evt) => {
      try {
        const msg: CADEvent = JSON.parse(evt.data);
        const e = msg.event;
        if (onRawEventRef.current) onRawEventRef.current(e, msg.data);

        if (
          e === 'unit_status_changed' ||
          e === 'unit_created' ||
          e === 'unit_updated' ||
          e === 'call_created' ||
          e === 'call_updated' ||
          e === 'call_deleted' ||
          e === 'personnel_assigned' ||
          e === 'personnel_removed' ||
          e === 'personnel_reassigned' ||
          e === 'unit_location_updated' ||
          e === 'unit_logon' ||
          e === 'unit_logoff' ||
          e === 'unit_dispatched' ||
          e === 'unit_emergency' ||
          e === 'unit_emergency_cancel' ||
          e === 'phone_call_received'
        ) {
          onUpdateRef.current();

          const soundConfig = soundConfigRef.current;
          if (soundConfig) {
            if (mode === 'dispatch') {
              if (e === 'call_created' && soundConfig.sound_dispatch_new_call) {
                playSound(soundConfig.sound_dispatch_new_call);
              } else if (e === 'unit_status_changed' && soundConfig.sound_dispatch_status_change) {
                playSound(soundConfig.sound_dispatch_status_change);
              }
            } else if (mode === 'mobile') {
              if (e === 'call_created' && soundConfig.sound_mobile_call_assigned) {
                playSound(soundConfig.sound_mobile_call_assigned);
              } else if ((e === 'call_updated' || e === 'unit_dispatched') && soundConfig.sound_mobile_call_update) {
                playSound(soundConfig.sound_mobile_call_update);
              } else if (e === 'unit_status_changed' && soundConfig.sound_mobile_call_update) {
                playSound(soundConfig.sound_mobile_call_update);
              }
            }
          }
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      if (closingRef.current) return; // intentional teardown — do not reconnect
      console.log('CAD WebSocket disconnected, reconnecting in 3s...');
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, [mode]);

  useEffect(() => {
    connect();
    return () => {
      closingRef.current = true;
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); wsRef.current = null; }
    };
  }, [connect]);
}
